import { t } from '@lingui/core/macro';
import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from 'modules/match-video/domain/assembler/constants';
import { ReviewCanvasContext, ReviewScene } from 'modules/match-video/domain/interfaces';
import {
  blockHeight,
  blockWidth,
  Box,
  browserSceneEnv,
  drawRisingBlock,
  fillBlock,
  MASK_PAD_EM,
  measureBlock,
  scaleAround,
  SceneEnv,
  skewX,
  TextBlock,
  tracePolygon,
} from 'modules/match-video/domain/scenes/kit';
import { GoalPlateData, SceneTeam } from 'modules/match-video/domain/scenes/scene-data';
import { SlashGoalPlateState, slashGoalPlateState } from './plate-state';
import { PLATE_BOTTOM, PLATE_LEFT, SLASH, SLASH_FONTS } from './style';

// `.b-plate` and its parts, px of the 1920×1080 frame.
const HEIGHT = 160;
const TOP = OUTPUT_HEIGHT - PLATE_BOTTOM - HEIGHT;
const CENTER_Y = TOP + HEIGHT / 2;
const RIGHT_LIMIT = OUTPUT_WIDTH - PLATE_LEFT;
/** `.b-tag`: padding 0 70 0 40, the right edge slanting to 82 % at the bottom. */
const TAG = { size: 72, min: 48, maxWidth: 600, padLeft: 40, padRight: 70 };
const TAG_SHAPE = [
  [0, 0],
  [1, 0],
  [0.82, 1],
  [0, 1],
] as const;
/** `.streak`: 260 × 6 white bars `skewX(-30deg)` at these tops inside the tag. */
const STREAK = { width: 260, height: 6, skew: -30, tops: [40, 84, 122] };
/** `.b-body`: tucked 14 px under the tag, padding 0 110 0 150, gap 26, cut 14 % / 94 %. */
const BODY = { overlap: 14, padLeft: 150, padRight: 110, gap: 26 };
const BODY_SHAPE = [
  [0.14, 0],
  [1, 0],
  [0.94, 1],
  [0, 1],
] as const;
const NUMBER = { size: 84 };
const PLAYER = { size: 56, min: 40, lineHeight: 1 };
const TEAM = { size: 30, min: 22, lineHeight: 1.5 };
/** The text column's masks inherit the page's 15 px font, so their `.08em` padding is 1.2 px. */
const TEXT_MASK_EM = 15;
/** `.b-min`: a dark cell `skewX(-12deg)` above the plate (bottom 250, line 64, padding 0 22). */
const MINUTE = { bottom: 250, height: 64, size: 40, min: 28, maxWidth: 300, padX: 22, skew: -12 };

export const SLASH_GOAL_PLATE_FONTS: readonly string[] = [
  SLASH_FONTS.tag(TAG.size),
  SLASH_FONTS.number(NUMBER.size),
  SLASH_FONTS.player(PLAYER.size),
  SLASH_FONTS.plateTeam(TEAM.size),
];

interface GoalPlateLayout {
  tag: Box;
  word: TextBlock;
  body: Box;
  number: { left: number; block: TextBlock } | null;
  player: TextBlock | null;
  team: TextBlock;
  text: { left: number; top: number; width: number };
  minute: { box: Box; block: TextBlock } | null;
}

function layoutGoalPlate(m: ReviewCanvasContext, data: GoalPlateData & { team: SceneTeam }): GoalPlateLayout {
  const word = measureBlock(m, t`Гол!`.toLocaleUpperCase(), {
    maxWidth: TAG.maxWidth,
    size: TAG.size,
    min: TAG.min,
    font: SLASH_FONTS.tag,
    lineHeight: HEIGHT / TAG.size,
  });
  const tag: Box = { left: PLATE_LEFT, top: TOP, width: TAG.padLeft + blockWidth(word) + TAG.padRight, height: HEIGHT };
  const bodyLeft = tag.left + tag.width - BODY.overlap;

  const numberText = data.player?.number === undefined ? null : String(data.player.number);
  const numberBlock = numberText
    ? measureBlock(m, numberText, { maxWidth: 400, size: NUMBER.size, min: NUMBER.size, font: SLASH_FONTS.number })
    : null;
  const numberWidth = numberBlock ? blockWidth(numberBlock) + BODY.gap : 0;
  const textMax = Math.max(0, RIGHT_LIMIT - bodyLeft - BODY.padLeft - BODY.padRight - numberWidth);

  const player = data.player
    ? measureBlock(m, data.player.name.toLocaleUpperCase(), {
        maxWidth: textMax,
        size: PLAYER.size,
        min: PLAYER.min,
        font: SLASH_FONTS.player,
        lineHeight: PLAYER.lineHeight,
      })
    : null;
  const team = measureBlock(m, data.team.name.toLocaleUpperCase(), {
    maxWidth: textMax,
    size: TEAM.size,
    min: TEAM.min,
    font: SLASH_FONTS.plateTeam,
    lineHeight: TEAM.lineHeight,
  });
  const textWidth = Math.max(player ? blockWidth(player) : 0, blockWidth(team));
  const maskPad = MASK_PAD_EM * TEXT_MASK_EM;
  const textHeight = (player ? blockHeight(player) + maskPad : 0) + blockHeight(team) + maskPad;

  const contentLeft = bodyLeft + BODY.padLeft;
  const minuteBlock = data.minuteLabel
    ? measureBlock(m, data.minuteLabel, {
        maxWidth: MINUTE.maxWidth,
        size: MINUTE.size,
        min: MINUTE.min,
        font: SLASH_FONTS.minute,
        lineHeight: MINUTE.height / MINUTE.size,
      })
    : null;

  return {
    tag,
    word,
    body: { left: bodyLeft, top: TOP, width: BODY.padLeft + numberWidth + textWidth + BODY.padRight, height: HEIGHT },
    number: numberBlock ? { left: contentLeft, block: numberBlock } : null,
    player,
    team,
    text: { left: contentLeft + numberWidth, top: CENTER_Y - textHeight / 2, width: textWidth },
    minute: minuteBlock
      ? {
          box: {
            left: PLATE_LEFT,
            top: OUTPUT_HEIGHT - MINUTE.bottom - MINUTE.height,
            width: blockWidth(minuteBlock) + 2 * MINUTE.padX,
            height: MINUTE.height,
          },
          block: minuteBlock,
        }
      : null,
  };
}

function drawMinute(ctx: ReviewCanvasContext, minute: NonNullable<GoalPlateLayout['minute']>, s: SlashGoalPlateState): void {
  if (s.minute.alpha <= 0) return;
  const { box, block } = minute;
  ctx.save();
  ctx.globalAlpha *= s.minute.alpha;
  ctx.translate(box.left + box.width / 2, box.top + box.height / 2 + s.minute.dy);
  skewX(ctx, MINUTE.skew);
  ctx.fillStyle = SLASH.ink;
  ctx.fillRect(-box.width / 2, -box.height / 2, box.width, box.height);
  ctx.fillStyle = SLASH.white;
  fillBlock(ctx, block, -box.width / 2 + MINUTE.padX, -box.height / 2, 'left');
  ctx.restore();
}

function drawBody(ctx: ReviewCanvasContext, layout: GoalPlateLayout, s: SlashGoalPlateState): void {
  if (s.body.alpha <= 0) return;
  const { body, text } = layout;
  ctx.save();
  ctx.globalAlpha *= s.body.alpha;
  ctx.translate(s.body.dx, 0);
  tracePolygon(ctx, body, BODY_SHAPE);
  ctx.fillStyle = SLASH.white;
  ctx.fill();
  // `clip-path` cuts the body's children too.
  ctx.clip();

  if (layout.number && s.number.alpha > 0) {
    const { block, left } = layout.number;
    ctx.save();
    ctx.globalAlpha *= s.number.alpha;
    ctx.fillStyle = SLASH.red;
    fillBlock(ctx, block, left + s.number.dx, CENTER_Y - block.lineHeight / 2, 'left');
    ctx.restore();
  }

  let top = text.top;
  if (layout.player) {
    ctx.fillStyle = SLASH.ink;
    drawRisingBlock(ctx, layout.player, { x: text.left, top, width: text.width, align: 'left', maskEm: TEXT_MASK_EM }, s.playerRise);
    top += blockHeight(layout.player) + MASK_PAD_EM * TEXT_MASK_EM;
  }
  ctx.fillStyle = SLASH.plateTeam;
  drawRisingBlock(ctx, layout.team, { x: text.left, top, width: text.width, align: 'left', maskEm: TEXT_MASK_EM }, s.teamRise);
  ctx.restore();
}

function drawTag(ctx: ReviewCanvasContext, layout: GoalPlateLayout, s: SlashGoalPlateState): void {
  if (s.tag.alpha <= 0) return;
  const { tag, word } = layout;
  ctx.save();
  ctx.globalAlpha *= s.tag.alpha;
  ctx.translate(s.tag.dx, 0);
  tracePolygon(ctx, tag, TAG_SHAPE);
  ctx.fillStyle = SLASH.red;
  ctx.fill();
  ctx.clip();

  ctx.fillStyle = SLASH.white;
  s.streaks.forEach((streak, i) => {
    if (streak.alpha <= 0) return;
    ctx.save();
    ctx.globalAlpha *= streak.alpha;
    ctx.translate(tag.left + streak.dx + STREAK.width / 2, tag.top + STREAK.tops[i] + STREAK.height / 2);
    skewX(ctx, STREAK.skew);
    ctx.fillRect(-STREAK.width / 2, -STREAK.height / 2, STREAK.width, STREAK.height);
    ctx.restore();
  });

  if (s.word.alpha > 0) {
    const wordLeft = tag.left + TAG.padLeft;
    ctx.save();
    ctx.globalAlpha *= s.word.alpha;
    scaleAround(ctx, wordLeft + blockWidth(word) / 2, CENTER_Y, s.word.scale);
    fillBlock(ctx, word, wordLeft, tag.top, 'left');
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Set B goal plate: the red «ГОЛ!» tag flies in with an overshoot, speed streaks run through it, the word slams in
 * and the plate shakes; the white body catches up, the number and the scorer slide in, the minute drops from above.
 * Exit: the whole plate flies off to the left. No scorer — no player line and no number.
 */
export async function createSportSlashGoalPlate(data: GoalPlateData, env: SceneEnv = browserSceneEnv): Promise<ReviewScene> {
  const fonts = data.minuteLabel ? [...SLASH_GOAL_PLATE_FONTS, SLASH_FONTS.minute(MINUTE.size)] : SLASH_GOAL_PLATE_FONTS;
  await env.loadFonts(fonts);
  const measurer = env.createCanvas(1, 1);
  // No team — an unpaired goal: only the tag is drawn, the body is laid out empty and skipped.
  const tagOnly = !data.team;
  const layout = layoutGoalPlate(measurer.ctx, { ...data, team: data.team ?? { name: '' } });
  measurer.release();

  return {
    draw: (ctx, timeSec, windowSec) => {
      const s = slashGoalPlateState(timeSec, windowSec);
      if (s.exit.alpha <= 0) return;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';
      ctx.globalAlpha = s.exit.alpha;
      ctx.translate(s.exit.dx, 0);

      if (layout.minute) drawMinute(ctx, layout.minute, s);

      ctx.save();
      ctx.translate(s.shake.dx, s.shake.dy);
      if (!tagOnly) drawBody(ctx, layout, s);
      drawTag(ctx, layout, s);
      ctx.restore();
    },
    dispose: () => undefined,
  };
}
